import { visualVerticalLineIndexes } from "./textOrientation";
import {
  capHeightFromMetrics,
  imageRunSize,
  isInlineImageRun,
} from "./textInlineReplacements";

export const TEXT_STYLE = {
  NORMAL: "normal",
  ITALIC: "italic",
  UNDERLINE: "underline",
  ITALIC_UNDERLINE: "italic_underline",
};

export const normalizeTextStyle = (value) => {
  switch (value) {
    case TEXT_STYLE.ITALIC:
    case TEXT_STYLE.UNDERLINE:
    case TEXT_STYLE.ITALIC_UNDERLINE:
      return value;
    case TEXT_STYLE.NORMAL:
    default:
      return TEXT_STYLE.NORMAL;
  }
};

export const isItalicTextStyle = (value) => {
  const textStyle = normalizeTextStyle(value);
  return (
    textStyle === TEXT_STYLE.ITALIC || textStyle === TEXT_STYLE.ITALIC_UNDERLINE
  );
};

export const isUnderlineTextStyle = (value) => {
  const textStyle = normalizeTextStyle(value);
  return (
    textStyle === TEXT_STYLE.UNDERLINE ||
    textStyle === TEXT_STYLE.ITALIC_UNDERLINE
  );
};

export const canvasFontCss = ({
  fontSize,
  fontFamily,
  italic = false,
  fontWeight = 400,
}) => {
  const weight = Number(fontWeight);
  const weightToken =
    Number.isFinite(weight) && weight > 0 ? Math.round(weight) : 400;
  return `${italic ? "italic " : ""}${weightToken} ${fontSize}px "${fontFamily}"`;
};

export const textEmphasisSx = (textStyle) => ({
  fontStyle: isItalicTextStyle(textStyle) ? "italic" : "normal",
  textDecoration: isUnderlineTextStyle(textStyle) ? "underline" : "none",
});

/** CSS-like letter-spacing in px; non-finite values become 0. */
export const letterSpacingPx = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Tracking that belongs in front of run `index`. Letter-spacing sits between
 * characters, including the cut where a line was split into highlight or
 * image runs. Run 0 has no leading gap.
 */
export const runBoundarySpacing = (letterSpacing, index) =>
  index > 0 ? letterSpacingPx(letterSpacing) : 0;

/** Canvas/CSS stroke width in px; missing or non-positive values mean no stroke. */
export const strokeWidthPx = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return parsed;
};

export const strokeColorValue = (value) => {
  const color = String(value || "").trim();
  return color || "#000000";
};

/** Highlight padding in px; missing or non-positive values mean no text background. */
export const backgroundPaddingPx = (value) => strokeWidthPx(value);

/** Corner radius in px; missing/invalid means auto (padding + 2). 0 is square. */
export const backgroundRadiusPx = (value) => {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
};

/** Destination-px radius. Auto uses already-scaled padding; explicit values use `scale`. */
export const resolvedBackgroundRadiusPx = ({
  backgroundRadius,
  padding = 0,
  scale = 1,
} = {}) => {
  const explicit = backgroundRadiusPx(backgroundRadius);
  if (explicit == null) return Math.max(0, padding + 2);
  return Math.max(0, explicit * (Number(scale) || 1));
};

const backgroundRadiusForRun = (style, base) =>
  style?.background != null && style.backgroundRadius != null
    ? style.backgroundRadius
    : base?.backgroundRadius;

export const backgroundColorValue = (value) => strokeColorValue(value);

export const textStrokeSx = ({
  strokeWidth = 0,
  strokeColor = "#000000",
  scale = 1,
} = {}) => {
  const width = strokeWidthPx(strokeWidth) * (Number(scale) || 1);
  if (width <= 0) return {};
  return {
    WebkitTextStroke: `${width}px ${strokeColorValue(strokeColor)}`,
    paintOrder: "stroke fill",
  };
};

export const DEFAULT_SHADOW_ANGLE = 45;

const finiteNonNegative = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

/**
 * Resolves the stored shadow fields (angle in degrees, 0 = right, 90 = down)
 * into canvas-ready offsets. Returns null when there is nothing to paint.
 */
export const resolveTextShadow = ({
  shadowColor,
  shadowAngle,
  shadowDistance,
  shadowBlur,
} = {}) => {
  const distance = finiteNonNegative(shadowDistance);
  const blur = finiteNonNegative(shadowBlur);
  if (distance <= 0 && blur <= 0) return null;
  const parsedAngle = Number(shadowAngle);
  const angle =
    shadowAngle === "" || shadowAngle == null || !Number.isFinite(parsedAngle)
      ? DEFAULT_SHADOW_ANGLE
      : parsedAngle;
  const radians = (angle * Math.PI) / 180;
  return {
    color: strokeColorValue(shadowColor),
    blur,
    offsetX: Math.cos(radians) * distance,
    offsetY: Math.sin(radians) * distance,
  };
};

/** How far a shadow reaches past the glyphs, so clips can open up by that much. */
export const textShadowInkPad = (shadow) =>
  shadow ? Math.hypot(shadow.offsetX, shadow.offsetY) + shadow.blur : 0;

export const textShadowSx = ({ shadow, scale = 1 } = {}) => {
  if (!shadow) return {};
  const nextScale = Number(scale) || 1;
  return {
    textShadow: `${shadow.offsetX * nextScale}px ${
      shadow.offsetY * nextScale
    }px ${shadow.blur * nextScale}px ${shadow.color}`,
  };
};

export const textBackgroundSx = ({
  backgroundPadding = 0,
  backgroundColor = "#000000",
  backgroundRadius,
  scale = 1,
  hugGlyphs = false,
  spread = false,
  spreadOffsetY = 0,
} = {}) => {
  const nextScale = Number(scale) || 1;
  const padding = backgroundPaddingPx(backgroundPadding) * nextScale;
  if (padding <= 0) return {};
  const radius = resolvedBackgroundRadiusPx({
    backgroundRadius,
    padding,
    scale: nextScale,
  });
  if (spread) {
    // Editing paints the pill with a spread shadow instead of padding: an
    // inline fragment does not honour the negative hug margin (only its first
    // and last fragment do), so padding would widen the glyph advance, wrap
    // the line earlier than the fitted layout and slide the text sideways.
    // A shadow costs no layout, so the caret sits exactly where the bake will.
    return {
      backgroundColor: backgroundColorValue(backgroundColor),
      borderRadius: `${Math.max(0, Math.min(radius, 999) - padding)}px`,
      boxShadow: `0 ${spreadOffsetY}px 0 ${padding}px ${backgroundColorValue(
        backgroundColor
      )}`,
      boxDecorationBreak: "clone",
      WebkitBoxDecorationBreak: "clone",
    };
  }
  return {
    backgroundColor: backgroundColorValue(backgroundColor),
    padding: `${padding}px`,
    ...(hugGlyphs ? { margin: `-${padding}px` } : {}),
    borderRadius: `${Math.min(radius, 999)}px`,
    boxDecorationBreak: "clone",
    WebkitBoxDecorationBreak: "clone",
  };
};

const fillRoundRect = (ctx, x, y, width, height, radius = 0) => {
  const nextRadius = Math.max(0, Math.min(radius, width / 2, height / 2));
  ctx.beginPath();
  if (typeof ctx.roundRect === "function") {
    ctx.roundRect(x, y, width, height, nextRadius);
  } else {
    ctx.moveTo(x + nextRadius, y);
    ctx.arcTo(x + width, y, x + width, y + height, nextRadius);
    ctx.arcTo(x + width, y + height, x, y + height, nextRadius);
    ctx.arcTo(x, y + height, x, y, nextRadius);
    ctx.arcTo(x, y, x + width, y, nextRadius);
    ctx.closePath();
  }
  ctx.fill();
};

const paintTextBackground = ({
  ctx,
  text,
  x,
  y,
  fontSize,
  align = "left",
  letterSpacing = 0,
  backgroundPadding = 0,
  backgroundColor,
  backgroundRadius,
}) => {
  const padding = backgroundPaddingPx(backgroundPadding);
  if (padding <= 0) return;
  const content = String(text || "");
  if (!content) return;
  const width = measureTextWidthWithLetterSpacing(ctx, content, letterSpacing);
  if (width <= 0) return;

  let left = x;
  if (align === "center") left = x - width / 2;
  else if (align === "right") left = x - width;

  // Same 1em box as FittedTextPreview (line-height: 1). Do not switch to
  // actualBoundingBox: lowercase runs like "gana" would lose the top padding.
  const ascent = fontSize * 0.78;
  const descent = fontSize * 0.22;
  const top = ctx.textBaseline === "top" ? y - padding : y - ascent - padding;
  const height =
    (ctx.textBaseline === "top" ? fontSize : ascent + descent) + padding * 2;
  const boxX = left - padding;
  const boxW = width + padding * 2;
  const radius = resolvedBackgroundRadiusPx({ backgroundRadius, padding });

  ctx.save();
  ctx.fillStyle = backgroundColorValue(backgroundColor);
  fillRoundRect(ctx, boxX, top, boxW, height, radius);
  ctx.restore();
};

/** Width of a run including CSS-like letter-spacing between characters. */
export const measureTextWidthWithLetterSpacing = (
  ctx,
  text,
  letterSpacing = 0
) => {
  const content = String(text || "");
  const base = ctx.measureText(content).width;
  const spacing = letterSpacingPx(letterSpacing);
  if (!spacing) return base;
  const charCount = Array.from(content).length;
  if (charCount <= 1) return base;
  return base + (charCount - 1) * spacing;
};

const paintTextWithLetterSpacing = (
  ctx,
  text,
  x,
  y,
  letterSpacing,
  { strokeWidth = 0, strokeColor, shadow = null } = {}
) => {
  const content = String(text || "");
  const spacing = letterSpacingPx(letterSpacing);
  const width = strokeWidthPx(strokeWidth);
  const paintRun = (paint) => {
    if (!spacing) {
      paint(content, x, y);
      return;
    }

    const chars = Array.from(content);
    const widths = chars.map((char) => ctx.measureText(char).width);
    const total =
      widths.reduce((sum, glyphWidth) => sum + glyphWidth, 0) +
      Math.max(0, chars.length - 1) * spacing;

    let startX = x;
    const align = ctx.textAlign;
    if (align === "center") startX = x - total / 2;
    else if (align === "right" || align === "end") startX = x - total;

    const previousAlign = ctx.textAlign;
    ctx.textAlign = "left";
    let cursor = startX;
    chars.forEach((char, index) => {
      paint(char, cursor, y);
      cursor += widths[index] + spacing;
    });
    ctx.textAlign = previousAlign;
  };

  const applyShadow = () => {
    if (!shadow) return;
    ctx.shadowColor = shadow.color;
    ctx.shadowBlur = shadow.blur;
    ctx.shadowOffsetX = shadow.offsetX;
    ctx.shadowOffsetY = shadow.offsetY;
  };

  if (width > 0) {
    const strokeRun = () => {
      ctx.strokeStyle = strokeColorValue(strokeColor);
      ctx.lineWidth = width;
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      ctx.miterLimit = 2;
      paintRun((run, runX, runY) => ctx.strokeText(run, runX, runY));
    };
    // Shadow first, cast by the fill alone: casting it from stroke and fill
    // too stacks translucent blur edges and reads darker than the browser
    // preview. A stroke-only shadow would come out hollow. The visible ink is
    // then painted on top with no shadow.
    if (shadow) {
      ctx.save();
      applyShadow();
      paintRun((run, runX, runY) => ctx.fillText(run, runX, runY));
      ctx.restore();
    }
    ctx.save();
    strokeRun();
    ctx.restore();
    paintRun((run, runX, runY) => ctx.fillText(run, runX, runY));
    return;
  }
  ctx.save();
  applyShadow();
  paintRun((run, runX, runY) => ctx.fillText(run, runX, runY));
  ctx.restore();
};

export const fillTextWithUnderline = ({
  ctx,
  text,
  x,
  y,
  fontSize,
  underline,
  align = "left",
  letterSpacing = 0,
  strokeWidth = 0,
  strokeColor,
  shadow = null,
  backgroundPadding = 0,
  backgroundColor,
  backgroundRadius,
  paintLayer = "all",
}) => {
  const spacing = letterSpacingPx(letterSpacing);
  const paintBackground = paintLayer !== "glyphs";
  const paintGlyphs = paintLayer !== "background";
  if (paintBackground) {
    paintTextBackground({
      ctx,
      text,
      x,
      y,
      fontSize,
      align,
      letterSpacing: spacing,
      backgroundPadding,
      backgroundColor,
      backgroundRadius,
    });
  }
  if (!paintGlyphs) return;
  paintTextWithLetterSpacing(ctx, text, x, y, spacing, {
    strokeWidth,
    strokeColor,
    shadow,
  });
  if (!underline) return;
  const width = measureTextWidthWithLetterSpacing(ctx, text, spacing);
  if (width <= 0) return;
  const thickness = Math.max(1, Math.round(fontSize * 0.06));
  let left = x;
  if (align === "center") left = x - width / 2;
  else if (align === "right") left = x - width;
  const top =
    ctx.textBaseline === "top"
      ? y + fontSize * 0.85
      : y + Math.max(1, Math.round(fontSize * 0.1));
  ctx.fillRect(left, top, width, thickness);
};

export const paintHighlightedLine = ({
  ctx,
  runs = [],
  x,
  y,
  fontSize,
  underline,
  letterSpacing = 0,
  base = {},
  paintLayer = "all",
  imagesByAssetRef = {},
}) => {
  const paintRuns = (layer) => {
    let cursorX = x;
    runs.forEach((run, index) => {
      cursorX += runBoundarySpacing(letterSpacing, index);
      if (isInlineImageRun(run)) {
        if (base.fontFamily) {
          ctx.font = canvasFontCss({
            fontSize,
            fontFamily: base.fontFamily,
            italic: Boolean(base.italic),
            fontWeight: base.fontWeight,
          });
        }
        const capHeight = capHeightFromMetrics(ctx.measureText("H"), fontSize);
        const size = imageRunSize({ capHeight, aspect: run.aspect });
        const image = run.image || imagesByAssetRef?.[run.assetRef] || null;
        if (layer !== "background" && image && size.width > 0) {
          ctx.drawImage(
            image,
            cursorX,
            y - size.height,
            size.width,
            size.height
          );
        } else if (layer !== "background" && !image) {
          fillTextWithUnderline({
            ctx,
            text: run?.text || run?.phrase || "",
            x: cursorX,
            y,
            fontSize,
            underline: false,
            align: "left",
            letterSpacing,
            strokeWidth: 0,
            paintLayer: layer,
          });
          cursorX += measureTextWidthWithLetterSpacing(
            ctx,
            run?.text || run?.phrase || "",
            letterSpacing
          );
          return;
        }
        cursorX += size.width;
        return;
      }
      const style = run?.style || {};
      const runFamily = style.fontFamily || base.fontFamily;
      const runWeight =
        Number(style.fontWeight) > 0
          ? Number(style.fontWeight)
          : base.fontWeight;
      const runItalic =
        style.textStyle != null
          ? isItalicTextStyle(style.textStyle)
          : Boolean(base.italic);
      if (runFamily) {
        ctx.font = canvasFontCss({
          fontSize,
          fontFamily: runFamily,
          italic: runItalic,
          fontWeight: runWeight,
        });
      }
      ctx.fillStyle = style.fill || base.color || "#000000";
      const runUnderline =
        style.textStyle != null
          ? isUnderlineTextStyle(style.textStyle)
          : Boolean(underline);
      fillTextWithUnderline({
        ctx,
        text: run?.text || "",
        x: cursorX,
        y,
        fontSize,
        underline: runUnderline,
        align: "left",
        letterSpacing,
        strokeWidth:
          style.stroke != null ? style.strokeWidth || 0 : base.strokeWidth || 0,
        strokeColor: style.stroke || base.strokeColor,
        shadow: base.shadow,
        backgroundPadding:
          style.background != null
            ? style.backgroundPadding || 0
            : base.backgroundPadding || 0,
        backgroundColor: style.background || base.backgroundColor,
        backgroundRadius: backgroundRadiusForRun(style, base),
        paintLayer: layer,
      });
      cursorX += measureTextWidthWithLetterSpacing(
        ctx,
        run?.text || "",
        letterSpacing
      );
    });
  };
  if (paintLayer === "all") {
    paintRuns("background");
    paintRuns("glyphs");
  } else {
    paintRuns(paintLayer);
  }
  if (base.fontFamily) {
    ctx.font = canvasFontCss({
      fontSize,
      fontFamily: base.fontFamily,
      italic: Boolean(base.italic),
      fontWeight: base.fontWeight,
    });
  }
};

export const paintVerticalTextColumn = ({
  ctx,
  lines = [],
  lineRuns = null,
  orientation,
  x,
  blockTop,
  lineAdvance,
  fontSize,
  underline,
  letterSpacing = 0,
  base = {},
}) => {
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  const paintColumn = (paintLayer) => {
    visualVerticalLineIndexes(lines.length, orientation).forEach(
      (index, visualIndex) => {
        const y = blockTop + visualIndex * lineAdvance;
        const runs = lineRuns?.[index];
        const run = Array.isArray(runs) && runs.length ? runs[0] : null;
        const style = run?.style || {};
        const glyph = run?.text != null ? run.text : lines[index];
        const runFamily = style.fontFamily || base.fontFamily;
        const runWeight =
          Number(style.fontWeight) > 0
            ? Number(style.fontWeight)
            : base.fontWeight;
        const runItalic =
          style.textStyle != null
            ? isItalicTextStyle(style.textStyle)
            : Boolean(base.italic);
        ctx.font = canvasFontCss({
          fontSize,
          fontFamily: runFamily || "sans-serif",
          italic: runItalic,
          fontWeight: runWeight,
        });
        ctx.fillStyle = style.fill || base.color || "#000000";
        const runUnderline =
          style.textStyle != null
            ? isUnderlineTextStyle(style.textStyle)
            : Boolean(underline);
        fillTextWithUnderline({
          ctx,
          text: glyph,
          x,
          y,
          fontSize,
          underline: runUnderline,
          align: "center",
          letterSpacing,
          strokeWidth:
            style.stroke != null
              ? style.strokeWidth || 0
              : base.strokeWidth || 0,
          strokeColor: style.stroke || base.strokeColor,
          shadow: base.shadow,
          backgroundPadding:
            style.background != null
              ? style.backgroundPadding || 0
              : base.backgroundPadding || 0,
          backgroundColor: style.background || base.backgroundColor,
          backgroundRadius: backgroundRadiusForRun(style, base),
          paintLayer,
        });
      }
    );
  };
  paintColumn("background");
  paintColumn("glyphs");
};
